import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '../../api/router';
import {
  LayoutDashboard,
  Users,
  ListOrdered,
  CreditCard,
  Activity,
  Search,
  RefreshCcw,
  Send,
  ExternalLink,
  Inbox,
  Lock,
  LogOut,
  AlertCircle,
} from 'lucide-react';
import { trpc } from '@/providers/trpc';

type Tab = 'overview' | 'users' | 'orders' | 'payments' | 'activity';

// 15 seconds — enough freshness for an internal panel without hammering the
// database. Bump to 5_000 if you want near-realtime; raise to 60_000 if the
// DB load becomes a concern.
const POLL_MS = 15_000;

/**
 * Admin panel — not linked from any user-facing surface. Reachable only by
 * typing `/admin` directly. Authorisation is server-side: every query is
 * `adminProcedure` so a stranger gets UNAUTHORIZED/FORBIDDEN and sees the
 * generic "not found" mask below.
 */
export default function Admin() {
  const [tab, setTab] = useState<Tab>('overview');
  const utils = trpc.useUtils();

  // `stats` doubles as the auth probe — if it errors with "password required"
  // we show the password gate; any other error renders a generic 404 mask so
  // strangers can't tell this URL hosts anything sensitive.
  const stats = trpc.admin.stats.useQuery(undefined, {
    refetchInterval: POLL_MS,
    retry: false,
  });

  if (stats.isError) {
    const msg = stats.error?.message ?? '';
    if (/password/i.test(msg)) {
      return (
        <PasswordGate
          onSuccess={() => {
            void utils.admin.stats.invalidate();
          }}
        />
      );
    }
    return <NotFoundMask />;
  }

  const handleAdminLogout = async () => {
    try {
      await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
      });
    } finally {
      void utils.admin.stats.invalidate();
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#0B0E14] text-[#F0F2F5] relative">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-[#8A93A7] text-xs hover:text-[#F0F2F5] transition-colors"
            >
              ← Site
            </Link>
            <span className="text-[#8A93A7] text-xs">·</span>
            <div className="flex items-center gap-2">
              <LayoutDashboard size={14} className="text-[#00D4FF]" />
              <h1 className="text-[#F0F2F5] text-base sm:text-lg font-bold tracking-tight">
                Admin
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px] sm:text-[11px] text-[#8A93A7]">
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full ${
                stats.isFetching ? 'bg-[#FFB020] animate-pulse' : 'bg-[#00E5A0]'
              }`}
            />
            <span>
              {stats.isFetching
                ? 'refreshing…'
                : stats.dataUpdatedAt
                  ? `live · ${new Date(stats.dataUpdatedAt).toLocaleTimeString()}`
                  : 'idle'}
            </span>
            <button
              onClick={() => stats.refetch()}
              className="p-1 hover:text-[#F0F2F5] transition-colors"
              aria-label="Refresh now"
              title="Refresh now"
            >
              <RefreshCcw size={12} />
            </button>
            <button
              onClick={handleAdminLogout}
              className="flex items-center gap-1 p-1 hover:text-[#FF4757] transition-colors"
              aria-label="Lock admin"
              title="Lock admin"
            >
              <LogOut size={12} />
            </button>
          </div>
        </div>

        {/* Stats grid (always visible above tabs) */}
        {stats.data && <StatsGrid data={stats.data} />}

        {/* Tab bar */}
        <div className="flex gap-1 sm:gap-2 mt-6 sm:mt-8 mb-5 overflow-x-auto scrollbar-hide -mx-4 sm:mx-0 px-4 sm:px-0">
          {(
            [
              ['overview', LayoutDashboard, 'Overview'],
              ['users', Users, 'Users'],
              ['orders', ListOrdered, 'Orders'],
              ['payments', CreditCard, 'Payments'],
              ['activity', Activity, 'Activity'],
            ] as const
          ).map(([id, Icon, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-[12px] font-medium whitespace-nowrap transition-all
                ${
                  tab === id
                    ? 'bg-[#00D4FF] text-[#0B0E14] shadow-md shadow-[rgba(0,212,255,0.25)]'
                    : 'glass-pill text-[#B5BBC9] hover:text-[#F0F2F5]'
                }`}
            >
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>

        {/* Active tab */}
        {tab === 'overview' && stats.data && <Overview data={stats.data} />}
        {tab === 'users' && <UsersTable />}
        {tab === 'orders' && <OrdersTable />}
        {tab === 'payments' && <PaymentsTable />}
        {tab === 'activity' && <ActivityFeed />}
      </div>
    </div>
  );
}

// ─── Generic 404-mask shown to non-admins ────────────────────────────────────

function NotFoundMask() {
  return (
    <div className="min-h-[100dvh] bg-[#0B0E14] text-[#F0F2F5] flex flex-col items-center justify-center px-6">
      <h1 className="text-6xl font-extrabold text-[#8A93A7] mb-4">404</h1>
      <p className="text-[#8A93A7] text-sm mb-6">Page not found</p>
      <Link
        to="/"
        className="text-[#00D4FF] text-sm hover:underline"
      >
        ← Back to home
      </Link>
    </div>
  );
}

// ─── Password gate ───────────────────────────────────────────────────────────

function PasswordGate({ onSuccess }: { onSuccess: () => void }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!login || !password) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ login, password }),
      });
      if (res.ok) {
        setPassword('');
        onSuccess();
        return;
      }
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.status === 503) {
        setError('Admin panel is not configured on the server.');
      } else if (res.status === 401) {
        setError('Invalid login or password.');
      } else {
        setError(data.error ?? `HTTP ${res.status}`);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#0B0E14] text-[#F0F2F5] flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
      <form
        onSubmit={submit}
        className="relative z-10 glass-card rounded-2xl p-8 sm:p-10 w-full max-w-sm"
      >
        <div className="flex items-center justify-center mb-5">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{
              background:
                'linear-gradient(135deg, rgba(0,212,255,0.2) 0%, rgba(0,212,255,0.05) 100%)',
              boxShadow: '0 8px 32px rgba(0,212,255,0.20)',
              color: '#00D4FF',
            }}
          >
            <Lock size={22} />
          </div>
        </div>
        <h1 className="text-[#F0F2F5] text-lg font-bold text-center mb-1">
          Admin access
        </h1>
        <p className="text-[#8A93A7] text-xs text-center mb-6">
          Sign in with your admin login and password.
        </p>

        <label className="text-[#8A93A7] text-[10px] uppercase tracking-wider font-medium mb-1.5 block">
          Login
        </label>
        <input
          type="text"
          value={login}
          onChange={e => setLogin(e.target.value)}
          autoFocus
          autoComplete="username"
          spellCheck={false}
          autoCapitalize="off"
          placeholder="admin"
          className="w-full h-11 glass-input rounded-xl px-4 text-[#F0F2F5] text-sm placeholder:text-[#8A93A7]/50 mb-4"
          disabled={busy}
        />

        <label className="text-[#8A93A7] text-[10px] uppercase tracking-wider font-medium mb-1.5 block">
          Password
        </label>
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          autoComplete="current-password"
          placeholder="••••••••"
          className="w-full h-11 glass-input rounded-xl px-4 text-[#F0F2F5] text-sm placeholder:text-[#8A93A7]/50 mb-3"
          disabled={busy}
        />

        {error && (
          <div className="flex items-start gap-2 mb-3 p-3 rounded-xl bg-[rgba(255,71,87,0.08)] border border-[rgba(255,71,87,0.25)]">
            <AlertCircle size={14} className="text-[#FF4757] flex-shrink-0 mt-0.5" />
            <p className="text-[#FF4757] text-[12px]">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !login || !password}
          className="w-full h-11 glass-btn text-[#0B0E14] font-bold text-sm rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? 'Verifying…' : 'Unlock'}
        </button>

        <p className="text-[#8A93A7] text-[10px] text-center mt-5 leading-relaxed">
          Session expires after 4 hours of inactivity.
        </p>
      </form>
    </div>
  );
}

// ─── Stats ───────────────────────────────────────────────────────────────────

type Stats = inferRouterOutputs<AppRouter>['admin']['stats'];

function StatsGrid({ data }: { data: Stats }) {
  const cards = [
    {
      label: 'Users · total',
      value: data.users.total.toLocaleString(),
      sub: `+${data.users.new24h} new · 24h`,
      color: '#00D4FF',
    },
    {
      label: 'Active · 7d',
      value: data.users.active7d.toLocaleString(),
      sub: 'returning users',
      color: '#00E5A0',
    },
    {
      label: 'Orders · total',
      value: data.orders.total.toLocaleString(),
      sub: `+${data.orders.last24h} · 24h`,
      color: '#00D4FF',
    },
    {
      label: 'Pipeline value',
      value: `$${data.orders.pipelineValueUSD.toLocaleString()}`,
      sub: 'new + contacted + in_progress',
      color: '#FFB020',
    },
    {
      label: 'Revenue · paid',
      value: `$${data.payments.completedUSD.toLocaleString()}`,
      sub: `+ ${data.payments.completedStars.toLocaleString()} ⭐`,
      color: '#00E5A0',
    },
    {
      label: 'Payments · pending',
      value: data.payments.pendingCount.toLocaleString(),
      sub: 'awaiting verification',
      color: '#FFB020',
    },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3">
      {cards.map(c => (
        <div
          key={c.label}
          className="glass-card rounded-xl p-3.5 sm:p-4"
        >
          <p className="text-[9px] sm:text-[10px] uppercase tracking-wider text-[#8A93A7] mb-1.5">
            {c.label}
          </p>
          <p
            className="font-extrabold text-xl sm:text-2xl leading-none"
            style={{ color: c.color }}
          >
            {c.value}
          </p>
          <p className="text-[10px] sm:text-[11px] text-[#8A93A7] mt-1.5 truncate">
            {c.sub}
          </p>
        </div>
      ))}
    </div>
  );
}

function Overview({ data }: { data: Stats }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="glass-card rounded-2xl p-5">
        <h3 className="text-[#F0F2F5] text-base font-semibold mb-4">
          Orders by status
        </h3>
        <div className="space-y-2">
          {Object.entries(data.orders.byStatus).map(([status, count]) => (
            <div
              key={status}
              className="flex items-center justify-between text-sm"
            >
              <span className="text-[#B5BBC9] capitalize">
                {status.replace('_', ' ')}
              </span>
              <span className="text-[#F0F2F5] font-semibold tabular-nums">
                {count.toLocaleString()}
              </span>
            </div>
          ))}
          {Object.keys(data.orders.byStatus).length === 0 && (
            <p className="text-[#8A93A7] text-sm italic">No orders yet.</p>
          )}
        </div>
      </div>

      <div className="glass-card rounded-2xl p-5">
        <h3 className="text-[#F0F2F5] text-base font-semibold mb-4">
          Activity · last 24h
        </h3>
        <div className="space-y-2 text-sm">
          <Row label="New users" value={data.users.new24h} />
          <Row label="New orders" value={data.orders.last24h} />
          <Row label="Chat messages" value={data.activity.messages24h} />
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#B5BBC9]">{label}</span>
      <span className="text-[#F0F2F5] font-semibold tabular-nums">
        {value.toLocaleString()}
      </span>
    </div>
  );
}

// ─── Users table ─────────────────────────────────────────────────────────────

function UsersTable() {
  const [search, setSearch] = useState('');
  const q = trpc.admin.usersList.useQuery(
    { limit: 100, search: search || undefined },
    { refetchInterval: POLL_MS, retry: false }
  );

  return (
    <div>
      <SearchBar value={search} onChange={setSearch} placeholder="Search by name, @username, TG-id, email, phone…" />
      <TableShell loading={q.isLoading} empty={!q.data?.length}>
        <table className="w-full text-[12px] sm:text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#8A93A7] border-b border-[rgba(255,255,255,0.06)]">
              <Th>User</Th>
              <Th>Contacts</Th>
              <Th className="text-right">Orders</Th>
              <Th className="text-right">Paid</Th>
              <Th>Last active</Th>
              <Th className="text-right">Open</Th>
            </tr>
          </thead>
          <tbody>
            {q.data?.map(u => (
              <tr
                key={String(u.id)}
                className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(0,212,255,0.04)]"
              >
                <Td>
                  <div className="flex items-center gap-2.5 min-w-0">
                    {u.photoUrl ? (
                      <img
                        src={u.photoUrl}
                        alt=""
                        className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <span
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold uppercase flex-shrink-0"
                        style={{
                          background:
                            'linear-gradient(135deg, rgba(0,212,255,0.25) 0%, rgba(0,212,255,0.08) 100%)',
                          color: '#00D4FF',
                        }}
                      >
                        {(u.firstName ?? u.telegramUsername ?? '?')[0]}
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="text-[#F0F2F5] font-medium truncate">
                        {[u.firstName, u.lastName]
                          .filter(Boolean)
                          .join(' ') || 'Telegram User'}
                      </p>
                      <p className="text-[#8A93A7] text-[10px] truncate">
                        {u.telegramUsername ? `@${u.telegramUsername} · ` : ''}
                        <span className="font-mono">{u.telegramUserId}</span>
                      </p>
                    </div>
                  </div>
                </Td>
                <Td>
                  <p className="text-[#B5BBC9] truncate max-w-[200px]">
                    {u.email ?? '—'}
                  </p>
                  <p className="text-[#8A93A7] text-[10px] truncate">
                    {u.phone ?? '—'}
                  </p>
                </Td>
                <Td className="text-right tabular-nums">
                  <span className="text-[#F0F2F5] font-semibold">
                    {u.ordersCount}
                  </span>
                </Td>
                <Td className="text-right tabular-nums">
                  <p className="text-[#00E5A0] font-semibold">
                    ${u.paidUSD.toLocaleString()}
                  </p>
                  {u.paidStars > 0 && (
                    <p className="text-[#FFB020] text-[10px]">
                      {u.paidStars} ⭐
                    </p>
                  )}
                </Td>
                <Td>
                  <span className="text-[#B5BBC9] text-[11px]">
                    {fmtRel(u.lastActive)}
                  </span>
                </Td>
                <Td className="text-right">
                  <a
                    href={`tg://user?id=${u.telegramUserId}`}
                    className="inline-flex items-center gap-1 text-[#00D4FF] hover:underline text-[11px]"
                    title="Open in Telegram"
                  >
                    <Send size={11} />
                    DM
                  </a>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
    </div>
  );
}

// ─── Orders table ────────────────────────────────────────────────────────────

const ORDER_STATUSES = ['new', 'contacted', 'in_progress', 'completed', 'cancelled'] as const;
type OrderStatus = (typeof ORDER_STATUSES)[number];

function OrdersTable() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<OrderStatus | 'all'>('all');
  const q = trpc.admin.ordersList.useQuery(
    {
      limit: 100,
      search: search || undefined,
      status: status === 'all' ? undefined : status,
    },
    { refetchInterval: POLL_MS, retry: false }
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="flex-1">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search by customer, service, description…"
            noMb
          />
        </div>
        <FilterPills
          value={status}
          onChange={v => setStatus(v as OrderStatus | 'all')}
          options={['all', ...ORDER_STATUSES]}
        />
      </div>
      <TableShell loading={q.isLoading} empty={!q.data?.length}>
        <table className="w-full text-[12px] sm:text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#8A93A7] border-b border-[rgba(255,255,255,0.06)]">
              <Th>#</Th>
              <Th>Customer</Th>
              <Th>Service</Th>
              <Th className="text-right">Price</Th>
              <Th>Status</Th>
              <Th>Created</Th>
            </tr>
          </thead>
          <tbody>
            {q.data?.map(o => (
              <tr
                key={String(o.id)}
                className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(0,212,255,0.04)] align-top"
              >
                <Td>
                  <span className="font-mono text-[11px] text-[#8A93A7]">
                    #{String(o.id)}
                  </span>
                </Td>
                <Td>
                  <p className="text-[#F0F2F5] font-medium">{o.customerName}</p>
                  <p className="text-[#8A93A7] text-[10px]">
                    {o.telegramUsername
                      ? `@${o.telegramUsername} · `
                      : ''}
                    <span className="font-mono">{o.telegramUserId}</span>
                  </p>
                  {o.description && (
                    <p className="text-[#B5BBC9] text-[11px] mt-1 line-clamp-2 max-w-[280px]">
                      {o.description}
                    </p>
                  )}
                </Td>
                <Td>
                  <p className="text-[#B5BBC9]">{o.serviceName}</p>
                  <p className="text-[#8A93A7] text-[10px] font-mono">
                    {o.serviceId}
                  </p>
                </Td>
                <Td className="text-right tabular-nums">
                  <p className="text-[#00D4FF] font-semibold">
                    ${o.price.toLocaleString()}
                  </p>
                  {o.budget && (
                    <p className="text-[#8A93A7] text-[10px]">{o.budget}</p>
                  )}
                </Td>
                <Td>
                  <StatusPill status={o.status} />
                </Td>
                <Td>
                  <span className="text-[#B5BBC9] text-[11px]">
                    {fmtRel(o.createdAt)}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
    </div>
  );
}

// ─── Payments table ──────────────────────────────────────────────────────────

const PAYMENT_STATUSES = ['pending', 'processing', 'completed', 'failed', 'refunded'] as const;
type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

function PaymentsTable() {
  const [status, setStatus] = useState<PaymentStatus | 'all'>('all');
  const q = trpc.admin.paymentsList.useQuery(
    {
      limit: 100,
      status: status === 'all' ? undefined : status,
    },
    { refetchInterval: POLL_MS, retry: false }
  );

  return (
    <div>
      <div className="flex justify-end mb-3">
        <FilterPills
          value={status}
          onChange={v => setStatus(v as PaymentStatus | 'all')}
          options={['all', ...PAYMENT_STATUSES]}
        />
      </div>
      <TableShell loading={q.isLoading} empty={!q.data?.length}>
        <table className="w-full text-[12px] sm:text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#8A93A7] border-b border-[rgba(255,255,255,0.06)]">
              <Th>#</Th>
              <Th>Order / customer</Th>
              <Th>Method</Th>
              <Th className="text-right">Amount</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Txn</Th>
            </tr>
          </thead>
          <tbody>
            {q.data?.map(p => (
              <tr
                key={String(p.id)}
                className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(0,212,255,0.04)] align-top"
              >
                <Td>
                  <span className="font-mono text-[11px] text-[#8A93A7]">
                    #{String(p.id)}
                  </span>
                </Td>
                <Td>
                  <p className="text-[#F0F2F5] font-medium">
                    #{p.orderId} · {p.serviceName}
                  </p>
                  <p className="text-[#8A93A7] text-[10px]">
                    {p.customerName}
                    {p.telegramUsername ? ` · @${p.telegramUsername}` : ''}
                  </p>
                </Td>
                <Td>
                  <span className="text-[#B5BBC9] capitalize">
                    {p.method.replace('_', ' ')}
                  </span>
                </Td>
                <Td className="text-right tabular-nums">
                  <p className="text-[#F0F2F5] font-semibold">
                    {p.amount.toLocaleString()} {p.currency === 'XTR' ? '⭐' : p.currency}
                  </p>
                </Td>
                <Td>
                  <StatusPill status={p.status} />
                </Td>
                <Td>
                  <span className="text-[#B5BBC9] text-[11px]">
                    {fmtRel(p.createdAt)}
                  </span>
                </Td>
                <Td>
                  {p.transactionId ? (
                    <code className="text-[10px] text-[#8A93A7] break-all">
                      {p.transactionId.slice(0, 16)}…
                    </code>
                  ) : (
                    <span className="text-[#8A93A7]">—</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
    </div>
  );
}

// ─── Activity feed (audit logs) ──────────────────────────────────────────────

function ActivityFeed() {
  const q = trpc.admin.recentActivity.useQuery(
    { limit: 100 },
    { refetchInterval: POLL_MS, retry: false }
  );

  return (
    <TableShell loading={q.isLoading} empty={!q.data?.length}>
      <table className="w-full text-[12px] sm:text-[13px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-[#8A93A7] border-b border-[rgba(255,255,255,0.06)]">
            <Th>When</Th>
            <Th>Action</Th>
            <Th>Entity</Th>
            <Th>By</Th>
            <Th>IP</Th>
            <Th>Details</Th>
          </tr>
        </thead>
        <tbody>
          {q.data?.map(a => (
            <tr
              key={String(a.id)}
              className="border-b border-[rgba(255,255,255,0.04)] align-top"
            >
              <Td>
                <span className="text-[#B5BBC9] text-[11px]">
                  {fmtRel(a.createdAt)}
                </span>
              </Td>
              <Td>
                <span className="text-[#00D4FF] font-mono text-[11px]">
                  {a.action}
                </span>
              </Td>
              <Td>
                <span className="text-[#B5BBC9]">
                  {a.entity}
                  {a.entityId ? ` #${a.entityId}` : ''}
                </span>
              </Td>
              <Td>
                <span className="text-[#8A93A7] font-mono text-[11px]">
                  {a.telegramUserId ?? '—'}
                </span>
              </Td>
              <Td>
                <span className="text-[#8A93A7] font-mono text-[10px]">
                  {a.ip ?? '—'}
                </span>
              </Td>
              <Td>
                {a.details ? (
                  <code className="text-[10px] text-[#8A93A7] block max-w-[300px] truncate">
                    {JSON.stringify(a.details)}
                  </code>
                ) : (
                  <span className="text-[#8A93A7]">—</span>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  );
}

// ─── Building blocks ─────────────────────────────────────────────────────────

function SearchBar({
  value,
  onChange,
  placeholder,
  noMb,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  noMb?: boolean;
}) {
  return (
    <div className={`relative ${noMb ? '' : 'mb-3'}`}>
      <Search
        size={14}
        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8A93A7] pointer-events-none"
      />
      <input
        type="search"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full glass-input rounded-xl pl-9 pr-4 h-10 text-[#F0F2F5] text-[13px] placeholder:text-[#8A93A7]/50"
      />
    </div>
  );
}

function FilterPills({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
}) {
  return (
    <div className="flex gap-1 overflow-x-auto scrollbar-hide flex-shrink-0">
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`px-3 py-1.5 rounded-full text-[11px] font-medium uppercase tracking-wider whitespace-nowrap transition-all
            ${
              value === opt
                ? 'bg-[#00D4FF] text-[#0B0E14]'
                : 'glass-pill text-[#B5BBC9] hover:text-[#F0F2F5]'
            }`}
        >
          {opt.replace('_', ' ')}
        </button>
      ))}
    </div>
  );
}

function TableShell({
  loading,
  empty,
  children,
}: {
  loading: boolean;
  empty: boolean;
  children: React.ReactNode;
}) {
  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-10 text-center text-[#8A93A7] text-sm">
        Loading…
      </div>
    );
  }
  if (empty) {
    return (
      <div className="glass-card rounded-2xl p-10 text-center">
        <Inbox size={28} className="text-[#8A93A7] mx-auto mb-3 opacity-40" />
        <p className="text-[#8A93A7] text-sm">No rows match the current filter.</p>
      </div>
    );
  }
  return (
    <div className="glass-card rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`font-medium px-3 sm:px-4 py-3 ${className}`}>{children}</th>
  );
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 sm:px-4 py-3 ${className}`}>{children}</td>;
}

function StatusPill({ status }: { status: string }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    new: { bg: 'rgba(0,212,255,0.12)', fg: '#00D4FF' },
    contacted: { bg: 'rgba(0,212,255,0.12)', fg: '#00D4FF' },
    in_progress: { bg: 'rgba(255,176,32,0.12)', fg: '#FFB020' },
    completed: { bg: 'rgba(0,229,160,0.12)', fg: '#00E5A0' },
    cancelled: { bg: 'rgba(138,147,167,0.12)', fg: '#8A93A7' },
    pending: { bg: 'rgba(255,176,32,0.12)', fg: '#FFB020' },
    processing: { bg: 'rgba(0,212,255,0.12)', fg: '#00D4FF' },
    failed: { bg: 'rgba(255,71,87,0.12)', fg: '#FF4757' },
    refunded: { bg: 'rgba(138,147,167,0.12)', fg: '#8A93A7' },
  };
  const c = colors[status] ?? colors.new;
  return (
    <span
      className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ background: c.bg, color: c.fg }}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

// Unused-imports placeholder: lucide ExternalLink kept for future row-actions.
void ExternalLink;

function fmtRel(d: Date | string | null): string {
  if (!d) return '—';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return '—';
  const diff = Date.now() - dt.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  const days = Math.floor(sec / 86400);
  if (days < 30) return `${days}d ago`;
  return dt.toLocaleDateString();
}
